import { useQuery } from "@tanstack/react-query";
import { formatBytes, formatCount, formatExactBytes, formatNumber } from "../../lib/format.ts";
import { nodeUsageQuery } from "../../lib/queries.ts";
import filePage from "../file/FilePage.module.css";
import ui from "../ui.module.css";
import styles from "./Usage.module.css";

/** Meta pills for a folder's subtree, held open by a placeholder until the figures land. */
export function FolderStorageMeta({ id }: { id: string }) {
  const { data } = useQuery(nodeUsageQuery(id));

  if (!data) {
    return (
      <li data-pending>
        <span className={`${ui.skeleton} ${styles.metaSkeleton}`} />
      </li>
    );
  }

  return (
    <>
      <li title={formatExactBytes(data.liveBytes)}>
        {formatBytes(data.liveBytes)} in {formatCount(data.files, "file", "files")}
      </li>
      {data.supersededBytes > 0 ? (
        <li title={formatExactBytes(data.supersededBytes)}>
          {formatBytes(data.supersededBytes)} superseded
        </li>
      ) : null}
    </>
  );
}

/** Storage and traffic for one file, beside its version list. */
export function FileUsageFacts({ id }: { id: string }) {
  const { data } = useQuery(nodeUsageQuery(id));

  if (!data) return <span className={`${ui.skeleton} ${styles.factsSkeleton}`} />;

  return (
    <dl className={`${styles.split} ${filePage.versionFacts}`}>
      <div>
        <dt>Live</dt>
        <dd title={formatExactBytes(data.liveBytes)}>{formatBytes(data.liveBytes)}</dd>
      </div>
      <div>
        <dt>Superseded</dt>
        <dd title={formatExactBytes(data.supersededBytes)}>{formatBytes(data.supersededBytes)}</dd>
      </div>
      <div>
        <dt>Requests this month</dt>
        <dd>{formatNumber(data.month.requests)}</dd>
      </div>
      <div>
        <dt>Served this month</dt>
        <dd title={formatExactBytes(data.month.egressBytes)}>
          {formatBytes(data.month.egressBytes)}
        </dd>
      </div>
    </dl>
  );
}
